import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiUrl } from '../http/api.constant';
import { LoginResponse, TokenResponse } from '../../shared/models/user.models';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private currentUserSubject: BehaviorSubject<LoginResponse>;
  public currentUser: Observable<LoginResponse>;
  isSSO = false;

  constructor(private http: HttpClient) {
    this.currentUserSubject = new BehaviorSubject<LoginResponse>(
      JSON.parse(localStorage.getItem('currentUser'))
    );
    this.currentUser = this.currentUserSubject.asObservable();
    this.isSSO = localStorage.getItem('isSSO') === 'true';
  }

  public get currentUserValue(): LoginResponse {
    return this.currentUserSubject.value;
  }

  login(username: string, password: string): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(ApiUrl.login, { username, password, website: true })
      .pipe(
        map((response) => {
          this.saveCurrentUser(response);
          return response;
        })
      );
  }

  saveCurrentUser(user: LoginResponse, isSSO = false): void {
    // store user details and jwt token in local storage to keep user logged in between page refreshes
    localStorage.setItem('currentUser', JSON.stringify(user));
    this.currentUserSubject.next(user);

    if (isSSO) {
      localStorage.setItem('isSSO', 'true');
      this.isSSO = true;
    }
  }

  refreshToken(): Observable<TokenResponse> {
    return this.http
      .post<TokenResponse>(ApiUrl.token_refresh, {
        refresh: this.currentUserValue.refresh,
      })
      .pipe(
        map((res) => {
          const user = this.currentUserValue;
          user.access = res.access;
          user.refresh = res.refresh;
          user.user = res.user;
          this.saveCurrentUser(user);
          return user;
        })
      );
  }

  verifyPassword(password: string): Observable<void> {
    return this.http.post<void>(ApiUrl.verify_password, { password });
  }

  logout(blacklist = true): void {
    const access = this.currentUserValue?.access;
    const refresh = this.currentUserValue?.refresh;

    // remove user from local storage to log user out
    localStorage.clear();
    this.currentUserSubject.next(null);

    if (!refresh) {
      return;
    }

    // blacklist current access/refresh token
    if (blacklist) {
      this.http
        .post<void>(ApiUrl.token_blacklist, {
          access,
          refresh,
        })
        .subscribe();
    }
  }

  isUserForceToChange(): boolean {
    const user = this.currentUserValue as LoginResponse;
    return (
      user &&
      user.user &&
      (user.user.expired_password || !user.user.default_password_changed)
    );
  }

  setFlagsAfterChangedPassword(): void {
    const user = this.currentUserValue as LoginResponse;
    user.user.expired_password = false;
    user.user.default_password_changed = true;
    user.user.show_expire_password_reminder = false;
    user.user.password_age_left_days = 90;
    localStorage.setItem('currentUser', JSON.stringify(user));
    this.currentUserSubject.next(user);
  }
}
